import { Component } from '@nicolas-riciotti/light2';

export default class BlockIframe extends Component {
  attached() {
    const src = this.getAttribute('src') || this.getAttribute('data-src');
    if (!src) return;

    const title = this.getAttribute('title') || this.getAttribute('data-title') || 'Embedded module';
    const loading = this.getAttribute('loading') || 'lazy';
    const autoplayOnView = this.hasAttribute('autoplay-on-view');
    const pauseOnExit = this.hasAttribute('pause-on-exit');
    const replayOnView = this.hasAttribute('replay-on-view');

    let iframe = null;
    let isReady = false;
    let preBuilt = false;
    let played = false;
    let messageId = 0;
    let queue = [];

    const targetOrigin = () => {
      try {
        return new URL(src, window.location.href).origin;
      } catch {
        return window.location.origin;
      }
    };

    const escapeAttr = (value) => String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('"', '&quot;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;');

    const post = (type, payload = {}) => {
      const message = {
        source: 'block-iframe',
        id: ++messageId,
        type,
        payload,
      };

      if (!isReady || !iframe?.contentWindow) {
        queue.push(message);
        return;
      }

      iframe.contentWindow.postMessage(message, targetOrigin());
    };

    const flushQueue = () => {
      const pending = [...queue];
      queue = [];

      pending.forEach((message) => {
        iframe?.contentWindow?.postMessage(message, targetOrigin());
      });
    };

    const buildIframe = () => {
      if (preBuilt) return;

      preBuilt = true;

      this.style.display = 'block';
      this.style.width = '100%';
      this.style.maxWidth = 'var(--block-iframe-max-width, 760px)';
      this.style.height = '100%';
      this.style.marginInline = 'auto';
      this.style.overflow = 'hidden';

      this.innerHTML = `
        <iframe
          src="${escapeAttr(src)}"
          title="${escapeAttr(title)}"
          loading="${escapeAttr(loading)}"
          scrolling="no"
          style="
            display: block;
            width: 100%;
            height: 100%;
            border: 0;
            overflow: hidden;
          ">
        </iframe>
      `;

      iframe = this.querySelector('iframe');

      iframe.addEventListener('load', () => {
        isReady = true;
        flushQueue();

        this.dispatchEvent(new CustomEvent('block-iframe:ready', {
          bubbles: true,
          detail: { src },
        }));
      });
    };

    const handleMessage = (event) => {
      if (!iframe || event.source !== iframe.contentWindow) return;

      const data = event.data;
      if (!data || data.source !== 'block-module') return;

      this.dispatchEvent(new CustomEvent(`block-iframe:${data.type}`, {
        bubbles: true,
        detail: data.payload || {},
      }));
    };

    const play = () => post('play');
    const pause = () => post('pause');
    const close = () => post('close');
    const selectScene = (index) => post('selectScene', { index });

    this.play = play;
    this.pause = pause;
    this.close = close;
    this.selectScene = selectScene;

    window.addEventListener('message', handleMessage);

    const preObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !preBuilt) {
        preObserver.disconnect();
        buildIframe();
      }
    }, {
      threshold: 0.01,
      rootMargin: '0px 0px 300px 0px',
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          if (pauseOnExit && played) {
            pause();
          }

          return;
        }

        buildIframe();

        if (!autoplayOnView) return;

        if (!played || replayOnView) {
          played = true;
          play();
        }

        if (!replayOnView) {
          observer.disconnect();
        }
      });
    }, {
      threshold: 0.15,
    });

    preObserver.observe(this);
    observer.observe(this);

    this._blockIframeCleanup = () => {
      preObserver.disconnect();
      observer.disconnect();
      window.removeEventListener('message', handleMessage);
    };
  }

  detached() {
    if (this._blockIframeCleanup) {
      this._blockIframeCleanup();
      this._blockIframeCleanup = null;
    }
  }
}

customElements.define('block-iframe', BlockIframe);